import { Typography, FormControl, FormControlLabel, Radio, RadioGroup } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import type { OneOfManyPollResponse } from '@tunasong/schemas'
import { isOneOfManyPoll, isOneOfManyPollResponse } from '@tunasong/schemas'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import { getProgress } from '../progress-util.js'
import { sortPollOptionsIndices } from '../sort.js'
import Progress from '../progress.js'
import { PollOptionName } from '../poll-option-name.js'
import { PlayAllButton, PollAudioProvider, WithOptionAudio } from '../option-audio.js'
import styles from './vote.module.css'

export const OneOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId, sort } = props

  invariant(isOneOfManyPoll(poll), `Poll must be a OneOfManyPoll`)

  const { entities: responses = [], isSuccess } = graphHooks.useEntityChildren<OneOfManyPollResponse>({
    parentId: poll.id,
    filter: isOneOfManyPollResponse,
  })
  const queriedResponse = responses.find(response => response.userId === userId)

  const { createEntity, isCreatingEntity, createEntityResult } = graphHooks.useEntityGraph()
  const updateEntity = graphHooks.useEntityUpdate<OneOfManyPollResponse>({ debounceDelay: 0 })

  /**
   * This user's response, from whichever source knows about it first. The children query lags
   * the create by a refetch, so the queried one is undefined for a beat afterwards — long
   * enough for a second click to take the create path and write a duplicate, and long enough
   * for the radios to render as though the vote had not happened.
   */
  const created = createEntityResult.data
  const userResponse = queriedResponse ?? (isOneOfManyPollResponse(created) ? created : undefined)

  const hasVoted = isSuccess && Boolean(userResponse)

  const canVote = poll.allowChange || !hasVoted
  /**
   * `hasCreatedEntity` used to sit in here as well, and it is a LATCH rather than an in-flight
   * signal: it stays true for the rest of the mount, so it disabled the control the instant a
   * first vote landed. That made `allowChange` — and the update path added to serve it — dead
   * code until the page was reloaded. `isCreatingEntity` is the real in-flight guard, and
   * `userResponse` above is what keeps the post-create window from writing a second response.
   */
  const readOnly = isCreatingEntity || !isSuccess || poll.closed || !canVote

  const handleChange = async (ev: unknown, val: string) => {
    if (!userId) {
      throw new Error(`userId must be set`)
    }
    const choice = parseInt(val, 10)

    /**
     * `allowChange` used to call `createEntity` unconditionally, so changing an answer wrote a
     * SECOND pollresponse for the same user. That inflates `allResponses.length` — the
     * getProgress denominator — and makes the tally wrong for everyone, not just the person
     * who changed their mind. One user, one response: a change is an update.
     */
    if (userResponse) {
      updateEntity(userResponse.id, { choice })
      return
    }

    const response = {
      type: 'pollresponse',
      pollType: 'one-of-many',
      choice,
    } satisfies OneOfManyPollResponse
    await createEntity({ parent: poll, entity: response })
  }

  const { name = 'Untitled', pollOptions = [], finalOption, closed } = poll

  /** One-of-many ignored "Randomize order of options in poll" entirely. GH #1968 */
  const optionIndices = sort
    ? sortPollOptionsIndices(poll, responses, 'random', `${userId}:${poll.id}`)
    : pollOptions.map((_, idx) => idx)

  const response = closed ? finalOption : userId ? (userResponse?.choice ?? null) : null
  const numResponses = Object.keys(responses).length

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography variant="subtitle1" className={styles.title}>
          <PollIcon /> {name}
        </Typography>
        <PlayAllButton options={poll.pollOptions} />
        {poll.closed && (
          <Typography variant="caption" color="secondary">
            The Poll is closed.
          </Typography>
        )}
        <Typography variant="caption">{numResponses} participants.</Typography>

        <FormControl className={styles.formControl}>
          <RadioGroup aria-label="options" name="options" value={response ?? ''} onChange={handleChange}>
            {optionIndices.map(idx => (
              <WithOptionAudio key={idx} option={pollOptions[idx]}>
                <FormControlLabel
                  disabled={readOnly}
                  value={idx}
                  control={<Radio />}
                  label={
                    <>
                      <PollOptionName option={pollOptions[idx]} withAudio={false} />
                      {hasVoted ? (
                        <Progress value={getProgress(poll, responses, idx)} selected={idx === finalOption} />
                      ) : null}
                    </>
                  }
                  className={styles.option}
                />
              </WithOptionAudio>
            ))}
          </RadioGroup>
        </FormControl>
      </div>
    </PollAudioProvider>
  )
}
